import { useCallback } from 'react';
import z from 'zod';

import { Box, Button, Flex, Heading, Image } from '@rover/kibble/core';
import { Form, useKibbleForm } from '@rover/kibble/forms';
import CheckboxField from '@rover/kibble/forms/fields/CheckboxField';
import MediaQuery from '@rover/kibble/styles/MediaQuery';
import DynamicModal from '@rover/react-lib/src/pages/training/components/DynamicModal';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { TrainingSearchServiceInterestSubmit } from '../../../events/analyticsEvents';

import headerImage from './service-interest-modal-header.png';

type SessionType = 'private' | 'group' | 'board-and-train';
type SelectedSessionType = SessionType | 'no-option-selected';

const schema = z.object({
  serviceInterests: z.array(z.enum(['private', 'group', 'board-and-train'])),
});

type ServiceInterestOption = {
  primaryLabel: string;
  secondaryLabel: string;
  value: SessionType;
};

const serviceInterestOptions: ServiceInterestOption[] = [
  {
    primaryLabel: '1:1 Training',
    secondaryLabel: 'At home with you and a trainer',
    value: 'private',
  },
  {
    primaryLabel: 'Group Training',
    secondaryLabel: 'In a social setting with other dogs',
    value: 'group',
  },
  {
    primaryLabel: 'Board and Train',
    secondaryLabel: "At a trainer's home to learn while they stay",
    value: 'board-and-train',
  },
];

type ServiceInterestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  searchQueryLocation?: string;
};

const ServiceInterestModal = ({
  isOpen,
  onClose,
  searchQueryLocation,
}: ServiceInterestModalProps): JSX.Element => {
  const handleSubmit = useCallback(
    ({ serviceInterests }) => {
      const selectedSessionTypes: SelectedSessionType[] =
        serviceInterests.length > 0 ? serviceInterests : ['no-option-selected'];
      emitAnalyticsEvent(
        new TrainingSearchServiceInterestSubmit({
          searchQueryLocation,
          selectedSessionTypes,
          dismissed: false,
        })
      );
      onClose();
    },
    [searchQueryLocation, onClose]
  );

  const { configureField, formProps, submitButtonProps } = useKibbleForm({
    schema,
    defaultValues: { serviceInterests: ['private'] },
    onValidSubmit: handleSubmit,
  });

  const handleDismiss = useCallback(() => {
    emitAnalyticsEvent(
      new TrainingSearchServiceInterestSubmit({
        searchQueryLocation,
        dismissed: true,
      })
    );
    onClose();
  }, [searchQueryLocation, onClose]);

  return (
    <DynamicModal
      isOpen={isOpen}
      onRequestClose={handleDismiss}
      mobileStyle={{ overlay: {} }}
      desktopSX={{ overlay: {} }}
    >
      <Flex
        flexDirection="column"
        gap={['0x', '4x']}
        width="100%"
        height="100%"
        justifyContent="flex-end"
        alignSelf="flex-start"
        mr="-4x"
        mb="-6x"
      >
        <Form {...formProps} hideRequiredFieldWarning>
          <Flex flexDirection="column" justifyContent="center" height="100%">
            <Image
              src={headerImage}
              description="Dog doing the paw trick"
              sx={{
                // Hides the image for non mobile layouts
                [MediaQuery.MOBILE_UP.toString()]: { display: 'none' },
                alignSelf: 'center',
                // Hide image on short-height screens so it doesn't push content below out of view
                '@media (max-height: 729px)': {
                  display: 'none',
                },
              }}
              height="175px"
              width="178px"
              mb="6x"
            />
            <Flex flexDirection="column" gap={['0x', '4x']}>
              <Heading size="300" mb={['1x', '0x', '0x']} mt="0x">
                What dog training are you interested in?
              </Heading>
              <Box
                sx={{
                  [MediaQuery.MOBILE_UP.toString()]: { pb: '2x', pt: '0x' },
                }}
                pt="6x"
              >
                <CheckboxField
                  {...configureField('serviceInterests')}
                  primaryLabel="What dog training are you interested in?"
                  primaryLabelA11yHidden
                  required={false}
                  options={serviceInterestOptions}
                />
              </Box>
              <Flex
                flexDirection="column"
                gap="3x"
                width="100%"
                sx={{
                  [MediaQuery.MOBILE_UP.toString()]: { mb: '0x', mt: '-6x' },
                }}
                mb="6x"
              >
                <Button width="100%" variant="primary" {...submitButtonProps}>
                  Search for trainers
                </Button>
                <Button width="100%" onPress={handleDismiss}>
                  Close
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Form>
      </Flex>
    </DynamicModal>
  );
};

export default ServiceInterestModal;
